<template>
  <b-modal
    :visible="visible"
    :title="$t('occurrenceMapModal.title')"
    size="lg"
    hide-footer
    centered
    @shown="onShown"
    @hidden="onHidden"
    @hide="$emit('update:visible', false)"
  >
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="!summary" class="text-center text-muted py-4">
      {{ $t('occurrenceMapModal.loadError') }}
    </div>

    <div v-else>
      <div class="occ-info-card">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <strong style="font-size: 14px">{{ summary.occurrence.requester_name }}</strong>
          <span
            class="occ-info-card__status"
            :style="{
              background: summary.occurrence.status == 1 ? '#d4edda' : '#f8d7da',
              color: summary.occurrence.status == 1 ? '#155724' : '#721c24',
            }"
          >{{ summary.occurrence.status == 1 ? $t('occurrence.map.open') : $t('occurrence.map.closed') }}</span>
        </div>

        <div class="text-muted mb-1" style="font-size: 11px">
          <span v-if="summary.occurrence.protocol">#{{ summary.occurrence.protocol }} &bull; </span>
          {{ $t('occurrence.map.open') }} {{ timeAgo(summary.occurrence.created_at) }}
          <span v-if="isFromChildNode">
            &bull; <b-badge variant="light-primary">{{ summary.occurrence.node_name }}</b-badge>
          </span>
        </div>

        <hr class="occ-info-card__hr">

        <div v-if="summary.occurrence.address" class="mb-1">
          <b>{{ $t('occurrence.map.addressLabel') }}</b>
          {{ summary.occurrence.address }}{{ summary.occurrence.number ? ', ' + summary.occurrence.number : '' }}{{ summary.occurrence.complement ? ' - ' + summary.occurrence.complement : '' }}<br>
          <span v-if="summary.occurrence.neighborhood" class="text-muted">
            {{ summary.occurrence.neighborhood }}{{ summary.occurrence.city ? ' - ' + summary.occurrence.city : '' }}{{ summary.occurrence.state ? '/' + summary.occurrence.state : '' }}
          </span>
        </div>

        <div v-if="summary.occurrence.requester_phone" class="mb-1">
          <b>{{ $t('occurrence.map.phoneLabel') }}</b> {{ $occurrencePhone(summary.occurrence) }}
        </div>

        <hr class="occ-info-card__hr">

        <div v-if="summary.occurrence.discipline" class="mb-25">
          <b>{{ $t('occurrence.map.disciplineLabel') }}</b> {{ summary.occurrence.discipline }}
        </div>
        <div v-if="summary.occurrence.dispatch_type" class="mb-25">
          <b>{{ $t('occurrence.map.typeLabel') }}</b> {{ summary.occurrence.dispatch_type }}
        </div>
        <div v-if="summary.occurrence.dispatch_reason" class="mb-25">
          <b>{{ $t('occurrence.map.reasonLabel') }}</b> {{ summary.occurrence.dispatch_reason }}
        </div>

        <div v-if="summary.occurrence.complaint" class="occ-info-card__complaint">
          "{{ summary.occurrence.complaint }}"
        </div>

        <div v-if="summary.occurrence.created_by" class="text-muted mt-1" style="font-size: 11px">
          {{ $t('occurrence.map.openedBy') }} {{ summary.occurrence.created_by }}
        </div>
      </div>

      <GmapMap
        v-if="summary.occurrence.latitude && summary.occurrence.longitude"
        :center="{ lat: parseFloat(summary.occurrence.latitude), lng: parseFloat(summary.occurrence.longitude) }"
        :zoom="14"
        style="width: 100%; height: 380px; border-radius: 8px"
      >
        <GmapMarker
          :position="{ lat: parseFloat(summary.occurrence.latitude), lng: parseFloat(summary.occurrence.longitude) }"
          :icon="occurrencePinIcon(summary.occurrence)"
          :z-index="100"
        />
        <GmapPolyline
          v-for="vehicle in vehiclesWithTrail"
          :key="'trail-' + vehicle.dispatch_id"
          :path="trailPath(vehicle.identifier_code)"
          :options="{ strokeColor: '#7367f0', strokeOpacity: 0.85, strokeWeight: 4 }"
        />
        <FleetVehicleMarker
          v-for="position in attendingPositions"
          :key="position.identifier_code"
          :position="position"
          :title="fleetMarkerTitle(position)"
          :z-index="10"
          :trail="trailPath(position.identifier_code)"
        />
      </GmapMap>
      <p v-else class="text-muted">
        {{ $t('occurrenceMapModal.noCoordinates') }}
      </p>

      <div class="map-summary-section">
        <h6 class="map-summary-section__title">
          {{ $t('occurrenceMapModal.vehiclesTitle') }}
        </h6>
        <div v-if="!summary.vehicles.length" class="text-muted" style="font-size: 12.5px">
          {{ $t('occurrenceMapModal.noVehicles') }}
        </div>
        <div
          v-for="vehicle in summary.vehicles"
          :key="vehicle.dispatch_id"
          class="map-summary-card"
        >
          <div class="map-summary-card__main">
            <strong>{{ vehicle.identifier_code || vehicle.team_name }}</strong>
            <span class="text-muted ml-1">{{ vehicle.team_name }}</span>
            <button
              v-if="vehicleCameras[vehicle.dispatch_id]"
              type="button"
              class="btn btn-sm btn-outline-primary ml-1 py-0"
              @click="openCameras(vehicle)"
            >
              <feather-icon icon="VideoIcon" size="12" class="mr-25" />{{ $t('dispatchFleet.cameras.button') }}
            </button>
          </div>
          <div class="map-summary-card__metric">
            <template v-if="vehicle.status_name">
              <span
                class="map-summary-card__position-dot"
                :style="{ background: vehicle.status_color || '#b4bdc6' }"
              />
              {{ $t('occurrenceMapModal.position') }}:
              <strong class="map-summary-card__position">
                {{ vehicle.status_code ? vehicle.status_code + ' — ' : '' }}{{ vehicle.status_name }}
              </strong>
              <span
                v-if="vehicle.status_at"
                :title="formatDateTime(vehicle.status_at)"
              >· {{ timeAgo(vehicle.status_at) }}</span>
            </template>
            <span
              v-else
              class="text-muted"
            >{{ $t('occurrenceMapModal.noPosition') }}</span>
          </div>
          <div v-if="vehicle.response_time_seconds !== null" class="map-summary-card__metric">
            <feather-icon icon="CheckCircleIcon" size="12" class="mr-25 text-success" />
            {{ $t('occurrenceMapModal.responseTime') }}: {{ formatDuration(vehicle.response_time_seconds) }}
          </div>
          <div v-else-if="vehicle.eta_to_scene_seconds !== null" class="map-summary-card__metric">
            <feather-icon icon="NavigationIcon" size="12" class="mr-25 text-warning" />
            {{ $t('occurrenceMapModal.etaToScene') }}: {{ vehicle.eta_to_scene_text || formatDuration(vehicle.eta_to_scene_seconds) }}
          </div>
          <div v-else class="map-summary-card__metric text-muted">
            {{ $t('occurrenceMapModal.etaUnavailable') }}
          </div>
        </div>
      </div>

      <div class="map-summary-section">
        <h6 class="map-summary-section__title">
          {{ $t('occurrenceMapModal.destinationsTitle') }}
        </h6>
        <div v-if="!summary.destinations.length" class="text-muted" style="font-size: 12.5px">
          {{ $t('occurrenceMapModal.noDestinations') }}
        </div>
        <div
          v-for="destination in summary.destinations"
          :key="destination.involved_id"
          class="map-summary-card"
        >
          <div class="map-summary-card__main">
            <feather-icon icon="MapPinIcon" size="12" class="mr-25 text-danger" />
            <strong>{{ destination.establishment_name || $t('occurrenceMapModal.noEstablishment') }}</strong>
          </div>
          <div v-if="destination.eta_seconds !== null" class="map-summary-card__metric">
            {{ $t('occurrenceMapModal.etaToDestination') }}: {{ destination.eta_text || formatDuration(destination.eta_seconds) }}
          </div>
          <div v-else class="map-summary-card__metric text-muted">
            {{ $t('occurrenceMapModal.etaUnavailable') }}
          </div>
        </div>
      </div>
    </div>

    <dispatch-cameras-modal
      v-if="camerasFor"
      :key="'cameras-' + camerasFor.dispatch_id"
      ref="camerasModal"
      :dispatch-id="camerasFor.dispatch_id"
      :team-name="camerasFor.team_name"
    />
  </b-modal>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import FleetPositionsMixin from '@/mixins/FleetPositionsMixin'
import DispatchEventsMixin from '@/mixins/DispatchEventsMixin'
import DispatchTrailMixin from '@/mixins/DispatchTrailMixin'
import OccurrencePinMixin from '@/mixins/OccurrencePinMixin'
import FleetVehicleMarker from '@/components/FleetVehicleMarker.vue'
import DispatchCamerasModal from '@/components/despacho/DispatchCamerasModal.vue'

export default {
  name: 'OccurrenceMapModal',

  components: { BSpinner, DispatchCamerasModal, FleetVehicleMarker },

  mixins: [ApiErrorMixin, FleetPositionsMixin, DispatchEventsMixin, DispatchTrailMixin, OccurrencePinMixin],

  props: {
    visible: { type: Boolean, default: false },
    occurrenceId: { type: [Number, String], default: null },
  },

  data() {
    return {
      loading: false,
      summary: null,
      // câmeras do Frotas por despacho (dispatch_id => true quando a equipe tem câmera) e a equipe com o modal aberto
      vehicleCameras: {},
      camerasFor: null,
    }
  },

  computed: {
    attendingCodes() {
      if (!this.summary) return []
      return this.summary.vehicles.map(v => v.identifier_code).filter(Boolean)
    },
    // Equipes que têm código do Frotas: são as que ganham trajeto no mapa.
    vehiclesWithTrail() {
      return this.summary ? this.summary.vehicles.filter(v => v.identifier_code && this.trails[v.identifier_code]) : []
    },
    attendingPositions() {
      return this.fleetPositionsOnMap.filter(p => this.attendingCodes.includes(p.identifier_code))
    },
    isFromChildNode() {
      if (!this.summary || !this.summary.occurrence.node_id) return false
      const selectedNodeId = this.$store.state.selected_node?.id
      return !!selectedNodeId && this.summary.occurrence.node_id !== selectedNodeId
    },
  },

  methods: {
    onShown() {
      this.loadSummary()
      this.loadFleetPositions()
      this.watchFleetPositions()
      this.watchDispatchEvents()
    },

    onHidden() {
      this.unwatchFleetPositions()
      this.unwatchDispatchEvents()
      this.summary = null
      this.trails = {}
      this.trailMeta = {}
      this.vehicleCameras = {}
      this.camerasFor = null
    },

    async loadSummary() {
      if (!this.occurrenceId) return
      this.loading = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrences/${this.occurrenceId}/attendance-map`)
        this.summary = res.data.data
        this.loadVehicleExtras()
      } catch (e) {
        this.summary = null
        this.$message(this.$t('common.error'), this.apiErrorText(e, this.$t('occurrenceMapModal.loadError')), 'error')
      } finally {
        this.loading = false
      }
    },

    // Trajeto desde o empenho e disponibilidade de câmera de cada equipe com código do Frotas. Falhas só escondem
    // a linha e o botão: o resumo do atendimento não depende disso.
    loadVehicleExtras() {
      const vehicles = this.summary.vehicles.filter(v => v.identifier_code && v.dispatch_id)

      vehicles.forEach(async v => {
        this.loadTrail(v.dispatch_id, v.identifier_code)

        try {
          const res = await this.$http.get(`${this.$store.state.api}occurrence_dispatches/${v.dispatch_id}/cameras`)
          this.$set(this.vehicleCameras, v.dispatch_id, !!(res.data.data && res.data.data.available))
        } catch (e) {
          this.$set(this.vehicleCameras, v.dispatch_id, false)
        }
      })
    },

    openCameras(vehicle) {
      this.camerasFor = vehicle
      this.$nextTick(() => this.$refs.camerasModal && this.$refs.camerasModal.show())
    },

    // Posição nova de uma equipe da ocorrência: entra na linha do trajeto. (Chamado pelo FleetPositionsMixin.)
    onFleetPositionsApplied(codes) {
      codes.filter(code => this.attendingCodes.includes(code)).forEach(code => {
        const position = this.fleetPositionsOnMap.find(p => p.identifier_code === code)
        if (position) this.appendTrailPoint(code, position.lat, position.lng)
      })
    },

    // Posição reportada (app da guarnição ou web) atualiza só o card da
    // equipe — recarregar o resumo gastaria cota do Distance Matrix à toa.
    onDispatchEvent(kind, payload) {
      if (kind !== 'position' || !this.summary) return

      this.summary.vehicles = this.summary.vehicles.map(v => (Number(v.team_id) === Number(payload.team_id)
        ? {
          ...v,
          status_code: payload.code,
          status_name: payload.name,
          status_color: payload.panel_color || null,
          status_at: payload.reported_at,
        }
        : v))
    },

    formatDateTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('pt-BR')
    },

    timeAgo(dateStr) {
      if (!dateStr) return ''
      const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
      if (diff < 60) return this.$t('occurrence.timeAgo.now')
      if (diff < 3600) return this.$t('occurrence.timeAgo.minutes', { m: Math.floor(diff / 60) })
      if (diff < 86400) return this.$t('occurrence.timeAgo.hours', { h: Math.floor(diff / 3600) })
      const d = Math.floor(diff / 86400)
      return d > 1 ? this.$t('occurrence.timeAgo.days', { d }) : this.$t('occurrence.timeAgo.day', { d })
    },

    formatDuration(seconds) {
      if (seconds === null || seconds === undefined) return '-'
      const total = Math.max(0, Math.round(seconds))
      const h = Math.floor(total / 3600)
      const m = Math.floor((total % 3600) / 60)
      const s = total % 60
      if (h > 0) return `${h}h ${m}min`
      if (m > 0) return `${m}min ${s}s`
      return `${s}s`
    },
  },
}
</script>

<style scoped>
.occ-info-card {
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 12px;
}

.occ-info-card__status {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
}

.occ-info-card__hr {
  margin: 4px 0 6px;
  border-color: #eee;
}

.occ-info-card__complaint {
  margin-top: 6px;
  padding: 5px 7px;
  background: #f8f9fa;
  border-radius: 4px;
  font-style: italic;
  color: #444;
}

.map-summary-section {
  margin-top: 14px;
}

.map-summary-section__title {
  font-size: 13px;
  font-weight: 700;
  color: #6e6b7b;
  margin-bottom: 6px;
}

.map-summary-card {
  padding: 8px 10px;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 12.5px;
}

.map-summary-card__main {
  margin-bottom: 2px;
}

.map-summary-card__metric {
  color: #6e6b7b;
}

.map-summary-card__position-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}

.map-summary-card__position {
  color: #5e5873;
}
</style>
